import * as Sentry from "@sentry/vue";
import { v4 as uuidv4, v5 as uuidv5 } from "uuid";
import { IS_DEV_ENV, IS_CYPRESS_ENV } from "./constants";
import { SUPPORTED_LOCALES } from "../types";
import i18n from "../i18n";

const reportError = (error: any, extraData: null | Record<string, any> = null, tags = {}, outputToConsole = false) => {
  // eslint-disable-next-line no-param-reassign
  extraData = extraData || {};

  if (error?.originalError) {
    // eslint-disable-next-line no-param-reassign
    extraData.originalErrorMessage = `${error.originalError}`;
  }

  if (typeof error === "string") {
    // eslint-disable-next-line no-param-reassign
    error = new Error(error);
  }

  if (IS_DEV_ENV || IS_CYPRESS_ENV || outputToConsole) {
    console.info(error, extraData);
  }

  Sentry.withScope((scope) => {
    scope.setFingerprint([error.message]);
    scope.setTags(tags);
    Sentry.setContext("extra_data", extraData);
    Sentry.captureException(error);
  });
};

const reportMessage = (message: string, extraData: null | Record<string, any> = null, outputToConsole = false) => {
  if (IS_DEV_ENV || IS_CYPRESS_ENV || outputToConsole) {
    console.info(message, extraData);
  }

  Sentry.setContext("extra_data", extraData || {});
  Sentry.captureMessage(message);
};

const getThrottledFunction = (fn: CallableFunction, intervalInMilliseconds: number) => {
  let isThrottled = false;

  return () => {
    if (isThrottled) {
      return;
    }

    fn();
    isThrottled = true;

    setTimeout(() => {
      isThrottled = false;
    }, intervalInMilliseconds);
  };
};

const wait = async (milliseconds: number) =>
  new Promise<void>((resolve) => {
    setTimeout(resolve, milliseconds);
  });

const isObjectAndJsonSerializable = (value: any): boolean => {
  try {
    if (!value || typeof value !== "object") {
      throw new Error(`"value" is not an object that can be serialized to JSON`);
    }

    JSON.stringify(value);
    return true;
  } catch (error: any) {
    reportError(`[isObjectAndJsonSerializable] Error serializing object`, { value, error });
    return false;
  }
};

const isJsonParseableString = (value: any): boolean => {
  try {
    if (typeof value !== "string") {
      throw new Error(`"value" is not a string that can be parsed to JSON`);
    }

    JSON.parse(value);
    return true;
  } catch (error: any) {
    reportError(`[isJsonParseableString] Error parsing string to JSON`, { value, error });
    return false;
  }
};

const generateUUID = (input = ""): string => {
  if (input) {
    const namespace = "34259ae5-13d7-4e92-a488-d15f24156158";
    return uuidv5(input, namespace);
  }
  return uuidv4();
};

const getBrowserLanguage = () => (navigator.language || "").toLowerCase().split("-")[0].split("_")[0];

const getSupportedLanguagesForDropdown = (): Record<string, any> =>
  Object.keys(SUPPORTED_LOCALES).map((key: string) => ({
    value: key,
    text: SUPPORTED_LOCALES[key as keyof typeof SUPPORTED_LOCALES],
  }));

const getUserFromLocalStorage = (): null | Record<string, any> => {
  const storedUserContent = localStorage.getItem("parlaeditor_user");
  return storedUserContent ? JSON.parse(storedUserContent) : null;
};

const getCurrentUserId = (): null | string => {
  const currentUser = getUserFromLocalStorage();
  return currentUser ? currentUser.id : null;
};

const isAdminEmail = (userEmail: string): boolean => {
  const regExpAdminUsers = [/mamede\.anderson(\+.+)?@gmail\.com/];
  return Boolean(regExpAdminUsers.find((regExp) => regExp.test(userEmail)));
};

const getAuthErrorMessage = (code: string): string => {
  const translationMessage = i18n.t(code);
  // if both are the same, then no translation is available
  return (translationMessage === code ? i18n.t("auth/general-error") : translationMessage).toString();
};

const isEmailRelatedErrorCode = (code: string): boolean => {
  if (!code) {
    return false;
  }

  const emailRelatedErrorCodes = ["auth/email-already-in-use", "auth/invalid-email", "auth/invalid-credentials"];
  return emailRelatedErrorCodes.includes(code) || code.includes("email");
};

const isPasswordRelatedErrorCode = (code: string): boolean => {
  if (!code) {
    return false;
  }

  const passwordRelatedErrorCodes = ["auth/invalid-credentials"];
  return passwordRelatedErrorCodes.includes(code) || code.includes("password");
};

const orderItemsByKey = (items: any[], key: string, orderType: string) => {
  const getNormalizedValue = (obj: any) => String(obj[key] || "").toLowerCase();

  const compare = (a: any, b: any) => {
    if (typeof a[key] === "undefined" || a[key] === null) {
      return 1;
    }

    if (typeof b[key] === "undefined" || b[key] === null) {
      return -1;
    }

    const valueA = getNormalizedValue(a);
    const valueB = getNormalizedValue(b);
    return orderType.toLowerCase() === "asc"
      ? valueA.localeCompare(valueB, "en", { sensitivity: "base" })
      : valueB.localeCompare(valueA, "en", { sensitivity: "base" });
  };

  return [...items].sort(compare);
};

/** Make a text safe to be used in HTML (prevent XSS) */
const escapeTextForSafeHtml = (text: string): string => text.replace(/</g, "&lt;");

/** Format seconds to MM:SS */
const formatSecondsToTime = (seconds: number) => {
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;

  const formattedMinutes = minutes < 10 ? `0${minutes}` : minutes;
  const formattedSeconds = remainingSeconds < 10 ? `0${remainingSeconds}` : remainingSeconds;

  return `${formattedMinutes}:${formattedSeconds}`;
};

/** Format seconds to HH:MM:SS */
const formatSecondsToFullTime = (seconds: number) => {
  const hours = Math.floor(seconds / 3600);
  const remainingMinutes = Math.floor((seconds % 3600) / 60);
  const remainingSeconds = seconds % 60;

  const formattedHours = hours < 10 ? `0${hours}` : hours;
  const formattedMinutes = remainingMinutes < 10 ? `0${remainingMinutes}` : remainingMinutes;
  const formattedSeconds = remainingSeconds < 10 ? `0${remainingSeconds}` : remainingSeconds;

  return `${formattedHours}:${formattedMinutes}:${formattedSeconds}`;
};

export {
  getThrottledFunction,
  wait,
  isObjectAndJsonSerializable,
  isJsonParseableString,
  reportError,
  reportMessage,
  getBrowserLanguage,
  getSupportedLanguagesForDropdown,
  generateUUID,
  getUserFromLocalStorage,
  getCurrentUserId,
  isAdminEmail,
  getAuthErrorMessage,
  isEmailRelatedErrorCode,
  isPasswordRelatedErrorCode,
  orderItemsByKey,
  escapeTextForSafeHtml,
  formatSecondsToTime,
  formatSecondsToFullTime,
};
